/**
 * Availability Calendar Modal Component
 * Displays monthly room availability & starting rates (Show only / Not clickable)
 * Fully localized with i18n (TH / EN / ZH) per Rule 15
 */

import { fetchCalendarAvailability, type CalendarAvailabilityData } from '../api';
import { t, formatYearMonth, getWeekdayHeaders } from '../i18n';

let currentYear: number = new Date().getFullYear();
let currentMonth: number = new Date().getMonth() + 1; // 1-12
let currentRoomTypeCode: string = 'all';
let cachedRoomTypes: Array<{ code: string; name: string }> = [];

/**
 * Open the Availability Calendar modal
 */
export function openAvailabilityCalendar(initialDate?: string): void {
  // Remove existing modal if any
  closeAvailabilityCalendar();

  if (initialDate && /^\d{4}-\d{2}/.test(initialDate)) {
    const [y, m] = initialDate.split('-').map(Number);
    currentYear = y;
    currentMonth = m;
  } else {
    const now = new Date();
    currentYear = now.getFullYear();
    currentMonth = now.getMonth() + 1;
  }
  currentRoomTypeCode = 'all';

  const overlay = document.createElement('div');
  overlay.id = 'avail-cal-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 99999;
    padding: 12px;
    box-sizing: border-box;
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
  `;

  overlay.innerHTML = `
    <div id="avail-cal-card" style="
      background: #ffffff;
      width: 100%;
      max-width: 390px;
      max-height: 92vh;
      border-radius: 10px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      box-sizing: border-box;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    ">
      <!-- Modal Header -->
      <div style="
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 14px 16px 10px;
      ">
        <span style="
          font-weight: 800;
          font-style: italic;
          font-size: 16px;
          letter-spacing: 0.5px;
          color: #1a1a1a;
          text-transform: uppercase;
        ">
          ${t('availabilityCalendar')}
        </span>
        <button id="avail-cal-close-x" type="button" aria-label="${t('close')}" style="
          width: 32px;
          height: 32px;
          background: #1a1a1a;
          color: #ffffff;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 18px;
          font-weight: bold;
          line-height: 1;
        ">✕</button>
      </div>

      <!-- Modal Body -->
      <div style="
        padding: 6px 16px 14px;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      ">
        <!-- Legend -->
        <div style="
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 12px;
          font-size: 13px;
          color: #333;
        ">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="
              width: 16px;
              height: 16px;
              background: #BEE57C;
              display: inline-block;
              border: 1px solid #a3cc60;
            "></span>
            <span>${t('available')}</span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="
              width: 16px;
              height: 16px;
              display: inline-block;
              border: 1px solid #cccccc;
              background:
                linear-gradient(to top right, transparent calc(50% - 1px), #999 calc(50% - 1px), #999 calc(50% + 1px), transparent calc(50% + 1px)),
                linear-gradient(to bottom right, transparent calc(50% - 1px), #999 calc(50% - 1px), #999 calc(50% + 1px), transparent calc(50% + 1px));
              background-color: #ffffff;
            "></span>
            <span>${t('notAvailable')}</span>
          </div>
        </div>

        <!-- Month Dropdown -->
        <div style="margin-bottom: 10px;">
          <select id="avail-cal-month-select" style="
            width: 100%;
            padding: 9px 12px;
            font-size: 14px;
            border: 1px solid #cfd8dc;
            border-radius: 4px;
            background: #fff;
            color: #263238;
            outline: none;
            box-sizing: border-box;
          "></select>
        </div>

        <!-- Room Type Dropdown -->
        <div style="margin-bottom: 14px;">
          <select id="avail-cal-roomtype-select" style="
            width: 100%;
            padding: 9px 12px;
            font-size: 14px;
            border: 1px solid #cfd8dc;
            border-radius: 4px;
            background: #fff;
            color: #263238;
            outline: none;
            box-sizing: border-box;
          ">
            <option value="all">${t('allRooms')}</option>
          </select>
        </div>

        <!-- Month & Year Title (Centered) -->
        <div id="avail-cal-month-title" style="
          text-align: center;
          font-weight: 700;
          font-size: 15px;
          margin-bottom: 10px;
          color: #222;
        ">
          ${formatYearMonth(currentYear, currentMonth)}
        </div>

        <!-- Calendar Container -->
        <div id="avail-cal-grid-wrap" style="position: relative; min-height: 220px;">
          <div id="avail-cal-loading" style="
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(255, 255, 255, 0.85);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 13px;
            color: #666;
            z-index: 10;
          ">
            ${t('loadingCalendar')}
          </div>
          <div id="avail-cal-grid-content"></div>
        </div>

        <!-- Prev / Next Buttons -->
        <div style="
          display: flex;
          gap: 12px;
          margin-top: 14px;
        ">
          <button id="avail-cal-prev-btn" type="button" style="
            flex: 1;
            padding: 10px 14px;
            background: #F8B49B;
            color: #fff;
            border: none;
            border-radius: 4px;
            font-weight: 700;
            font-size: 14px;
            cursor: default;
            opacity: 0.7;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            transition: background 0.2s ease, opacity 0.2s ease;
          ">
            ${t('prevMonth')}
          </button>
          <button id="avail-cal-next-btn" type="button" style="
            flex: 1;
            padding: 10px 14px;
            background: #E85A1D;
            color: #fff;
            border: none;
            border-radius: 4px;
            font-weight: 700;
            font-size: 14px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          ">
            ${t('nextMonth')}
          </button>
        </div>

        <!-- CLOSE Button -->
        <div style="margin-top: 12px;">
          <button id="avail-cal-close-btn" type="button" style="
            width: 100%;
            padding: 11px;
            background: #fff;
            color: #1a1a1a;
            border: 1.5px solid #1a1a1a;
            border-radius: 4px;
            font-weight: 800;
            font-size: 14px;
            text-transform: uppercase;
            cursor: pointer;
            letter-spacing: 0.5px;
          ">
            ${t('close')}
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  document.body.style.overflow = 'hidden';

  // Attach event listeners
  const closeX = document.getElementById('avail-cal-close-x');
  const closeBtn = document.getElementById('avail-cal-close-btn');
  closeX?.addEventListener('click', closeAvailabilityCalendar);
  closeBtn?.addEventListener('click', closeAvailabilityCalendar);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      closeAvailabilityCalendar();
    }
  });

  populateMonthDropdown();
  if (cachedRoomTypes.length > 0) {
    populateRoomTypesDropdown(cachedRoomTypes);
  }

  const monthSelect = document.getElementById('avail-cal-month-select') as HTMLSelectElement | null;
  monthSelect?.addEventListener('change', () => {
    if (monthSelect.value) {
      const [y, m] = monthSelect.value.split('-').map(Number);
      currentYear = y;
      currentMonth = m;
      updateMonthTitle();
      updateNavButtons();
      loadCalendarData();
    }
  });

  const roomTypeSelect = document.getElementById('avail-cal-roomtype-select') as HTMLSelectElement | null;
  roomTypeSelect?.addEventListener('change', () => {
    currentRoomTypeCode = roomTypeSelect.value || 'all';
    loadCalendarData();
  });

  const prevBtn = document.getElementById('avail-cal-prev-btn') as HTMLButtonElement | null;
  prevBtn?.addEventListener('click', () => {
    if (prevBtn.disabled) return;
    currentMonth -= 1;
    if (currentMonth < 1) {
      currentMonth = 12;
      currentYear -= 1;
    }
    syncMonthSelect();
    updateMonthTitle();
    updateNavButtons();
    loadCalendarData();
  });

  const nextBtn = document.getElementById('avail-cal-next-btn') as HTMLButtonElement | null;
  nextBtn?.addEventListener('click', () => {
    currentMonth += 1;
    if (currentMonth > 12) {
      currentMonth = 1;
      currentYear += 1;
    }
    syncMonthSelect();
    updateMonthTitle();
    updateNavButtons();
    loadCalendarData();
  });

  // Initial load
  updateNavButtons();
  loadCalendarData();
}

/**
 * Close the Availability Calendar modal
 */
export function closeAvailabilityCalendar(): void {
  const overlay = document.getElementById('avail-cal-overlay');
  if (overlay) {
    overlay.remove();
  }
  document.body.style.overflow = '';
}

function updateMonthTitle(): void {
  const titleEl = document.getElementById('avail-cal-month-title');
  if (titleEl) {
    titleEl.textContent = formatYearMonth(currentYear, currentMonth);
  }
}

/**
 * Update Prev/Next button states and colors.
 * When on or before current month, Prev button is disabled (muted salmon #F8B49B).
 * When on a future month, Prev button becomes ACTIVE (vibrant orange #E85A1D, matching Next button).
 */
function updateNavButtons(): void {
  const prevBtn = document.getElementById('avail-cal-prev-btn') as HTMLButtonElement | null;
  if (!prevBtn) return;

  const now = new Date();
  const isAtOrBeforeCurrentMonth =
    currentYear < now.getFullYear() ||
    (currentYear === now.getFullYear() && currentMonth <= now.getMonth() + 1);

  if (isAtOrBeforeCurrentMonth) {
    // Inactive / Disabled
    prevBtn.disabled = true;
    prevBtn.style.background = '#F8B49B';
    prevBtn.style.cursor = 'default';
    prevBtn.style.opacity = '0.7';
  } else {
    // Active / Clickable -> Changes color to vibrant active orange!
    prevBtn.disabled = false;
    prevBtn.style.background = '#E85A1D';
    prevBtn.style.cursor = 'pointer';
    prevBtn.style.opacity = '1';
  }
}

function populateMonthDropdown(): void {
  const monthSelect = document.getElementById('avail-cal-month-select') as HTMLSelectElement | null;
  if (!monthSelect) return;

  monthSelect.innerHTML = '';
  const now = new Date();
  const startY = now.getFullYear();
  const startM = now.getMonth(); // 0-11

  for (let i = 0; i < 12; i++) {
    const d = new Date(startY, startM + i, 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const val = `${y}-${String(m).padStart(2, '0')}`;
    const opt = document.createElement('option');
    opt.value = val;
    opt.textContent = formatYearMonth(y, m);
    if (y === currentYear && m === currentMonth) {
      opt.selected = true;
    }
    monthSelect.appendChild(opt);
  }
}

function syncMonthSelect(): void {
  const monthSelect = document.getElementById('avail-cal-month-select') as HTMLSelectElement | null;
  if (!monthSelect) return;

  const val = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
  let found = false;
  for (let i = 0; i < monthSelect.options.length; i++) {
    if (monthSelect.options[i].value === val) {
      monthSelect.selectedIndex = i;
      found = true;
      break;
    }
  }

  if (!found) {
    const opt = document.createElement('option');
    opt.value = val;
    opt.textContent = formatYearMonth(currentYear, currentMonth);
    opt.selected = true;
    monthSelect.appendChild(opt);
  }
}

function populateRoomTypesDropdown(roomTypes: Array<{ code: string; name: string }>): void {
  const select = document.getElementById('avail-cal-roomtype-select') as HTMLSelectElement | null;
  if (!select) return;

  cachedRoomTypes = roomTypes;
  select.innerHTML = '';

  for (const rt of roomTypes) {
    const opt = document.createElement('option');
    opt.value = rt.code;
    opt.textContent = rt.code === 'all' ? t('allRooms') : rt.name;
    if (rt.code === currentRoomTypeCode) {
      opt.selected = true;
    }
    select.appendChild(opt);
  }
}

async function loadCalendarData(): Promise<void> {
  const loadingEl = document.getElementById('avail-cal-loading');
  const gridContent = document.getElementById('avail-cal-grid-content');
  if (loadingEl) loadingEl.style.display = 'flex';

  const monthParam = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;

  try {
    const res: CalendarAvailabilityData | null = await fetchCalendarAvailability(monthParam, currentRoomTypeCode);

    if (res && Array.isArray(res.roomTypes) && res.roomTypes.length > 0) {
      populateRoomTypesDropdown(res.roomTypes);
    }

    renderCalendarGrid(res ? res.days : {}, currentYear, currentMonth);
  } catch (err) {
    console.error('[AvailCal] loadCalendarData error:', err);
    if (gridContent) {
      gridContent.innerHTML = `<div style="text-align:center;padding:20px;color:#d32f2f;">Failed to load calendar.</div>`;
    }
  } finally {
    if (loadingEl) loadingEl.style.display = 'none';
  }
}

function renderCalendarGrid(
  daysData: Record<string, { isAvailable: boolean; startingRate?: number; currency?: string }>,
  year: number,
  month: number
): void {
  const gridContent = document.getElementById('avail-cal-grid-content');
  if (!gridContent) return;

  const firstDayOfWeek = new Date(year, month - 1, 1).getDay(); // 0 = Sunday, 1 = Monday...
  const totalDaysInMonth = new Date(year, month, 0).getDate();

  // Previous month padding days
  const prevMonthTotalDays = new Date(year, month - 1, 0).getDate();

  const daysOfWeek = getWeekdayHeaders();

  let html = `
    <table style="
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      font-size: 13px;
      user-select: none;
      -webkit-user-select: none;
    ">
      <thead>
        <tr>
          ${daysOfWeek.map(d => `
            <th style="
              text-align: center;
              padding: 6px 0;
              font-weight: 600;
              font-size: 13px;
              color: #333;
            ">${d}</th>
          `).join('')}
        </tr>
      </thead>
      <tbody>
  `;

  let currentDay = 1;
  let nextMonthDay = 1;
  const totalRows = Math.ceil((firstDayOfWeek + totalDaysInMonth) / 7);

  for (let r = 0; r < totalRows; r++) {
    html += `<tr>`;
    for (let c = 0; c < 7; c++) {
      const cellIndex = r * 7 + c;

      if (cellIndex < firstDayOfWeek) {
        // Day from previous month
        const prevDayNum = prevMonthTotalDays - (firstDayOfWeek - cellIndex - 1);
        html += `
          <td style="
            border: 1px solid #e0e0e0;
            height: 48px;
            text-align: center;
            vertical-align: middle;
            color: #d0d0d0;
            background: #fafafa;
            pointer-events: none;
            cursor: default;
          ">
            <span style="font-size: 13px;">${prevDayNum}</span>
          </td>
        `;
      } else if (currentDay <= totalDaysInMonth) {
        // Day in current month
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`;
        const dayInfo = daysData[dateStr];
        const isAvailable = dayInfo?.isAvailable === true;
        const rate = dayInfo?.startingRate;

        if (isAvailable && rate !== undefined) {
          // Available day: Light green, date on top, starting price below
          html += `
            <td style="
              border: 1px solid #99c257;
              height: 48px;
              background: #BEE57C;
              text-align: center;
              vertical-align: middle;
              padding: 2px;
              box-sizing: border-box;
              pointer-events: none;
              cursor: default;
            ">
              <div style="font-size: 13px; font-weight: 700; color: #1a1a1a; line-height: 1.1;">${currentDay}</div>
              <div style="font-size: 10px; font-weight: 600; color: #2e4d0d; margin-top: 2px; line-height: 1;">
                ${rate.toLocaleString()}
              </div>
            </td>
          `;
        } else {
          // Not available day: White box with diagonal cross (X)
          html += `
            <td style="
              border: 1px solid #dcdcdc;
              height: 48px;
              text-align: center;
              vertical-align: middle;
              padding: 2px;
              box-sizing: border-box;
              position: relative;
              background:
                linear-gradient(to top right, transparent calc(50% - 1px), #b5b5b5 calc(50% - 1px), #b5b5b5 calc(50% + 1px), transparent calc(50% + 1px)),
                linear-gradient(to bottom right, transparent calc(50% - 1px), #b5b5b5 calc(50% - 1px), #b5b5b5 calc(50% + 1px), transparent calc(50% + 1px));
              background-color: #ffffff;
              pointer-events: none;
              cursor: default;
            ">
              <span style="
                position: relative;
                z-index: 2;
                font-size: 13px;
                color: #555;
                font-weight: 500;
              ">${currentDay}</span>
            </td>
          `;
        }
        currentDay++;
      } else {
        // Day from next month
        html += `
          <td style="
            border: 1px solid #e0e0e0;
            height: 48px;
            text-align: center;
            vertical-align: middle;
            color: #d0d0d0;
            background: #fafafa;
            pointer-events: none;
            cursor: default;
          ">
            <span style="font-size: 13px;">${nextMonthDay}</span>
          </td>
        `;
        nextMonthDay++;
      }
    }
    html += `</tr>`;
  }

  html += `
      </tbody>
    </table>
  `;

  gridContent.innerHTML = html;
}
